// src/admin/companies/companyModel.js — pure rules of the superadmin «Aziende» page and its purge dialog (v212).
// No React, no i18n: returns i18n keys (admin namespace) + params. Decisions C3–C5:
// plans/2026-10-11-1340-purge-fe-C-analysis.md.
import { firstBlocker } from '../purge/purgeModel.js';

const has = (preview, code) => Array.isArray(preview?.blockers) && preview.blockers.includes(code);

const rowCount = (preview, table) => {
  const n = preview?.rows?.[table];
  return typeof n === 'number' ? n : 0;
};

// C4 — offered only for a company KNOWN to be deactivated whose tenants are ALL known to be retired. A safety gate:
// unknown never passes (the "null fails open" rule does not apply to an irreversible action's precondition).
export function purgeGate(company, tenants) {
  if (company?.is_active !== false) {
    return { allowed: false, reasonKey: 'companyDetail.purgeDisabledActive', params: {} };
  }
  if (!Array.isArray(tenants)) {
    return { allowed: false, reasonKey: 'companyDetail.purgeDisabledUnknown', params: {} };
  }
  const active = tenants.filter((tn) => tn?.is_active !== false).length;
  if (active > 0) {
    return { allowed: false, reasonKey: 'companyDetail.purgeDisabledTenants', params: { count: active } };
  }
  return { allowed: true, reasonKey: null, params: {} };
}

// C3 — the erase preview (the "probe") loads on open. Erase is unavailable with live Stripe data, or when a purge
// was started in close mode (the erase preview then names PURGE_MODE_MISMATCH).
export function eraseUnavailableKey(eraseProbe) {
  if (has(eraseProbe, 'ERASE_REFUSED_LIVE_DATA')) return 'purge.company.eraseLiveData';
  if (has(eraseProbe, 'PURGE_MODE_MISMATCH')) return 'purge.company.eraseMismatch';
  return null;
}

// C5 — a started purge is finished in the mode it started in. Only called once the purge is KNOWN to have started:
// the erase preview names PURGE_MODE_MISMATCH exactly when it started in close.
export function startedMode(eraseProbe) {
  return has(eraseProbe, 'PURGE_MODE_MISMATCH') ? 'close' : 'erase';
}

// An open subscription with no usable Stripe key answers 502 on every attempt: it must be closed by hand first.
export function stripeNeedsManualClose(preview) {
  const open = preview?.stripe?.open_subscriptions;
  return Array.isArray(open) && open.length > 0 && preview?.stripe?.key_mode === 'none';
}

// What locks the dialog for the chosen mode: `{ code }` (worded via adminErrorCopy, TENANT_PROTECTED by the company
// sentence), `{ key }` (our own sentence), or null.
export function companyBlocker(preview) {
  const code = firstBlocker(preview);
  if (code) return { code };
  if (stripeNeedsManualClose(preview)) return { key: 'purge.company.stripeNoKey' };
  return null;
}

// The «Verranno eliminati:» list. `tenantNames` come from GET /customers/{id}/tenants (the preview has no names).
export function companyPreviewLines(preview, mode, tenantNames = []) {
  const names = Array.isArray(tenantNames) ? tenantNames.filter(Boolean) : [];
  const lines = [
    names.length > 0
      ? { key: 'purge.company.tenants', params: { names: names.join(', ') } }
      : { key: 'purge.company.noTenants', params: {} },
  ];
  const runs = rowCount(preview, 'content_generator.pipeline_runs');
  if (runs > 0) lines.push({ key: 'purge.groups.runs', params: { count: runs } });
  if (mode === 'erase') lines.push({ key: 'purge.company.billingErased', params: {} });
  const open = preview?.stripe?.open_subscriptions;
  if (Array.isArray(open) && open.length > 0 && !stripeNeedsManualClose(preview)) {
    lines.push({ key: 'purge.company.stripeOpen', params: { count: open.length } });
  }
  if (preview?.stripe?.delete_customer === true) lines.push({ key: 'purge.company.stripeDeleteCustomer', params: {} });
  return lines;
}

// companies_without_workspace[] of a tenant / person preview or result. Always an array.
export function orphanCompanies(payload) {
  const list = payload?.companies_without_workspace;
  return Array.isArray(list) ? list.filter((c) => c && c.customer_id) : [];
}

// One sentence per orphan; «va disattivata prima» only when it is KNOWN to be active.
export function orphanLineKeys(list) {
  return (Array.isArray(list) ? list : []).map((c) => ({
    key: c.is_active === true ? 'purge.orphan.lineActive' : 'purge.orphan.line',
    params: { name: c.legal_name || c.customer_id },
  }));
}

// last_admin_of[] of a person preview: the ACTIVE companies behind CANNOT_REMOVE_LAST_CUSTOMER_ADMIN. Always an array
// (an older backend sends none: the dialog keeps the generic sentence).
export function lastAdminCompanies(preview) {
  const list = preview?.last_admin_of;
  return Array.isArray(list) ? list.filter((c) => c && c.customer_id) : [];
}

// GET /admin/users/{id}/customers → its rows, or null when unknown (not an array: an older backend, a failed read).
export function userCompanies(payload) {
  return Array.isArray(payload) ? payload.filter((c) => c && c.customer_id) : null;
}

// The company through which the person keeps a tenant after leaving it: an `admin` row on the ACTIVE company that
// owns the tenant (a deactivated company grants nothing). null when there is none, or it is not known.
export function companyKeepingAccess(tenantCustomerId, companies) {
  if (!tenantCustomerId || !Array.isArray(companies)) return null;
  return companies.find((c) => c.customer_id === tenantCustomerId && c.role === 'admin' && c.is_active === true) || null;
}

export const companyPath = (customerId) => `/admin/companies/${encodeURIComponent(customerId)}`;
